import { formatDate } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { ButtonComponent } from "@controls/button/button.component";
import { AuthService } from "@core/services/auth.service";
import {
  AnnualStatementPageFilter,
  AnnualStatementStatus,
  SigningStatus
} from "@models/annual-statements/annual-statement-enums";
import { AnnualStatementRecord } from "@models/annual-statements/annual-statement-record";
import { DocumentSubFolderName } from "@models/common/document-sub-folder-name.enum";
import { ExportTypeEnum } from '@models/common/export-type.enum';
import { SubAnnualMenuItem } from "@models/ui-models/section-form-menu";
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import {
  getAnnualStatementsPageConfig,
  IAnnualStatementsPageConfig
} from "@pages/annual-statements/components/annual-statements-page/annual-statements-page-configs";
import {
  AnnualViewAndSendModalComponent
} from "@pages/annual-statements/components/annual-statements-page/components/annual-view-and-send-modal/annual-view-and-send-modal.component";
import { AnnualStatementsExportService } from "@pages/annual-statements/services/annual-statements-export.service";
import { AnnualStatementsGridService } from "@pages/annual-statements/services/annual-statements-grid.service";
import {
  CommonActionsModalComponent,
  IActionButtonConfig
} from "@shared/components/commons/common-actions-modal/common-actions-modal.component";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import { ConfirmModalComponent } from "@shared/components/commons/confirm/confirm-modal.component";
import {
  AdHocPaymentReminderComponent
} from "@shared/components/entity-profiles/ad-hoc-reminders/ad-hoc-payment-reminder/ad-hoc-payment-reminder.component";
import {
  AdHocSigningReminderComponent
} from "@shared/components/entity-profiles/ad-hoc-reminders/ad-hoc-signing-reminder/ad-hoc-signing-reminder.component";
import {
  AgAnnualActionsComponent
} from "@shared/components/grid/ag-cells/annual-statements/ag-annual-actions/ag-annual-actions.component";
import {
  GridServerSideComponent
} from "@shared/components/grid/components/grid-server-side/grid-server-side.component";
import { annualStatementsQueryParamKey } from "@shared/constants/query-param-keys.constant";
import { HasRoleDirective } from "@shared/directives/has-role.directive";
import { AnnualStatementsApiService } from "@shared/services/annual-statements-api.service";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { FilesApiService } from "@shared/services/files-api.service";
import { MarkSentSignedCommonService } from "@shared/services/mark-sent-signed.common.service";
import { MenuService } from "@shared/services/menu.service";
import { OrganisationApiService } from "@shared/services/organisation-api.service";
import { PageSizeService } from "@shared/services/page-size.service";
import { TagsService } from "@shared/services/tags.service";
import { downloadBase64File } from "@shared/utils/download-base64-file";
import { GetRowIdFunc, GetRowIdParams, GridApi } from "ag-grid-community";
import { GridOptions } from "ag-grid-enterprise";
import { ToastrService } from "ngx-toastr";
import { catchError, filter, of, switchMap, take, tap } from "rxjs";
import { ModalFormsService } from "../../../../forms/services/modal-forms.service";

@Component({
  selector: 'app-annual-statements-page',
  standalone: true,
  imports: [
    ButtonComponent,
    GridServerSideComponent,
    HasRoleDirective,
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle
  ],
  providers: [AnnualStatementsGridService, AnnualStatementsExportService],
  templateUrl: 'annual-statements-page.component.html',
  styleUrl: 'annual-statements-page.component.scss'
})
export class AnnualStatementsPageComponent extends CommonPageComponent implements OnInit {
  annualStatementsApiService = inject(AnnualStatementsApiService);
  annualStatementsGridService = inject(AnnualStatementsGridService);
  annualStatementsExportService = inject(AnnualStatementsExportService);
  authService = inject(AuthService);
  tagsService = inject(TagsService);
  companiesApiService = inject(CompaniesApiService);
  filesApiService = inject(FilesApiService);
  organisationService = inject(OrganisationApiService);
  modalFormsService = inject(ModalFormsService);
  markSentSignedCommonService = inject(MarkSentSignedCommonService);
  modalService = inject(NgbModal);
  menuService = inject(MenuService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  toastr = inject(ToastrService);
  destroyRef = inject(DestroyRef);

  pageConfig!: IAnnualStatementsPageConfig;

  records = this.annualStatementsGridService.records;
  checkedAnnualStatements: AnnualStatementRecord[] = [];
  canBeOpened = false;
  bulkMenuActionItems: SubAnnualMenuItem[] = [];

  override ngOnInit(): void {
    this.setupPageConfig();
    this.loadTags();
  }

  // =====  Initialising  =====

  setupPageConfig(): void {
    this.headerService.setHeaderLoading();

    this.route.data
      .pipe(take(1))
      .subscribe((data) => {
        this.pageConfig = getAnnualStatementsPageConfig(data['key']);
        this.headerService.setHeaderData(this.pageConfig.headerData);

        this.canBeOpened = this.authService.hasRole(['Admin', 'Manager', 'Preparer']);
        this.menuService.setMenuItemOpenState('Annual Statements');

        this.setGridConfig();
        this.setGridFilterOptions();
        this.setBulkMenuActionItems();
      });
  }

  loadTags(): void {
    this.isLoading.set(true);
    this.tagsService.getTags().subscribe({
      next: (tags) => {
        this.tagsList = Array.from(new Set(tags?.map(tag => tag.name)));
        this.setGridConfig();
        this.setGridFilterOptions();
        this.isLoading.set(false);
      },
      error: (error) => {
        this.isLoading.set(false);
        console.warn('[loadTags] error: ', error);
      }
    });
  }

  setBulkMenuActionItems(): void {
    const bulkMenuActionItems = [
      {
        title: 'Export to xlsx',
        pages: [],
        action: () => this.exportSelectedAnnualStatementsToXls()
      },
      // {
      //   name: 'Send in Bulk',
      //   pages: [AnnualStatementPageFilter.ReadyToSend],
      //   isVisible: () => false,
      //   action: () => this.sendInBulk()
      // },
      {
        title: 'Mark as Paid in bulk',
        pages: [AnnualStatementPageFilter.PaymentDue, AnnualStatementPageFilter.Overdue],
        action: () => this.maskAsPaidBulk()
      },
      {
        title: 'Mark as Sent in bulk',
        pages: [AnnualStatementPageFilter.ReadyToSend],
        isVisible: () => this.isMarkAsSentVisible(),
        action: () => this.markAsSentBulk()
      },
      {
        title: 'Mark as Signed in bulk',
        pages: [AnnualStatementPageFilter.ReadyToSend, AnnualStatementPageFilter.Sent],
        isVisible: () => this.isMarkAsSignedVisible(),
        action: () => this.markAsSignedBulkAction()
      },
      {
        title: 'Re-generate annual statement',
        pages: [AnnualStatementPageFilter.ReadyToSend, AnnualStatementPageFilter.NotReceived],
        action: () => this.openSelectRegenerateOptionModal()
      },
      {
        title: 'Download annual statement in bulk',
        pagesExclude: [AnnualStatementPageFilter.NotReceived, AnnualStatementPageFilter.Expected],
        action: () => this.downloadDocumentsBulk()
      },
      {
        title: 'Ignore in bulk',
        pages: [AnnualStatementPageFilter.NotReceived],
        action: () => this.ignoreBulk()
      },
      {
        title: 'Send for eSign in bulk',
        pages: [AnnualStatementPageFilter.ReadyToSend],
        action: () => this.bulkSendForSigning()
      },
    ];

    this.bulkMenuActionItems = bulkMenuActionItems.filter(item => {
      const pages = item.pages ?? [];
      const pagesExclude = item.pagesExclude ?? [];

      const inPages = pages.length === 0 || pages.includes(this.pageConfig.key);
      const notExcluded = !pagesExclude.includes(this.pageConfig.key);

      const visible = typeof item.isVisible === 'function'
        ? item.isVisible()
        : item.isVisible !== false;

      return inPages && notExcluded && visible;
    });
  }

  // =====  Actions  =====

  openAnnualViewAndSendModal(annualStatementId: string | null): void {
    if (!annualStatementId) {
      this.toastr.error('Document id not found', 'Error');
      return;
    }

    const modalRef = this.modalFormsService.openModal(AnnualViewAndSendModalComponent);
    const componentInstance = modalRef.componentInstance as AnnualViewAndSendModalComponent;

    componentInstance.documentId = annualStatementId;
    componentInstance.confirm
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        componentInstance['forceClose'] = true;
        componentInstance.close();
        this.reloadGrid();
      });
  }

  downloadDocuments(record: AnnualStatementRecord): void {
    this.filesApiService.getFiles(record.documentId)
      .pipe(
        switchMap((files) => {
          if (files.final.length) {
            return this.filesApiService.downloadFolder(record.documentId, false, DocumentSubFolderName.Final)
              .pipe(
                tap((folder) => {
                  if (folder) {
                    const currentDate = formatDate(new Date, 'dd_MM_yyyy', 'en-US');
                    const fileName = 'Annual_Statements_' + currentDate + '.zip';
                    downloadBase64File(folder, fileName);
                    this.toastr.success("Annual Statement have been success downloaded", "Success");
                  }
                }),
                catchError((error: HttpErrorResponse) => {
                  console.error(error);
                  this.toastr.error("Download Annual Statement error", "Error");
                  return of('ERR');
                })
              );
          }

          this.toastr.warning("No documents to download", "Warning");

          return of(files);
        }),
      )
      .subscribe({
        error: (error: HttpErrorResponse) => {
          console.error(error);
          this.toastr.error("Download documents error", "Error");
        }
      });
  }

  downloadCombinedPDF(): void {
    const selectedIds = this.checkedAnnualStatements.map(x => x.documentId);

    if (selectedIds.length > 1) {
      this.toastr.warning("Only one document is allowed to download as combined", "Warning");
    }

    this.filesApiService.downloadFolder(selectedIds[0], true, DocumentSubFolderName.Final)
      .subscribe({
        next: (folder) => {
          if (folder) {
            const currentDate = formatDate(new Date, 'dd_MM_yyyy', 'en-US');
            const fileName = 'Annual_Statements_' + currentDate + '.pdf';
            downloadBase64File(folder, fileName);
            this.toastr.success("Annual Statement have been success downloaded", "Success");
          }
        },
        error: (error: HttpErrorResponse) => {
          console.error(error);
          this.toastr.error("Download Annual Statement error", "Error");
          return of('ERR');
        }
      });
  }

  openArchiveModal(annualStatementRecord: AnnualStatementRecord): void {
    if (annualStatementRecord.status !== AnnualStatementStatus.Finalized) {
      const modalRef = this.modalService.open(ConfirmModalComponent);
      const componentInstance = modalRef.componentInstance as ConfirmModalComponent;

      componentInstance.title = 'Archive warning';
      componentInstance.message = 'Once you change the status to "Archive",' +
        ' you will have limited options for managing the annual statement. This means you will not be able to refresh' +
        ' the debt or send the annual statement. If annual statements have already been sent for eSigning' +
        ' and are still in progress, the eSigning process will be cancelled. Payment reminders will not be sent either.\n' +
        'Would you like to continue?';
      componentInstance.confirmText = 'Yes';
      componentInstance.closeText = 'No';

      componentInstance.confirm = () => {
        this.archive(annualStatementRecord);
        return of(true);
      };
    } else {
      this.archive(annualStatementRecord);
    }
  }

  archive(record: AnnualStatementRecord): void {
    this.annualStatementsApiService.archive(record.documentId)
      .subscribe({
        next: () => {
          this.reloadGrid();
          this.toastr.success("Annual statement is archived", "Success");
        },
        error: (err) => {
          this.toastr.error("Error happen.", "Error");
          console.error(err);
          return of('ERR');
        }
      });
  }

  ignore(companyId: string): void {
    this.annualStatementsApiService.ignore(companyId)
      .subscribe({
        next: () => {
          this.reloadGrid();
          this.toastr.success("Annual statement is ignored", "Success");
        },
        error: (err) => {
          this.toastr.error("Error happen.", "Error");
          console.error(err);
        }
      });
  }

  openAdHocSigningReminderModal(companyId: string): void {
    const annualRow = this.records().filter(row => row.companyId === companyId)[0];

    this.annualStatementsApiService.getAnnualById(annualRow.documentId)
      .pipe(
        switchMap(annual => {
          const modalRef = this.modalFormsService.openModal(AdHocSigningReminderComponent);
          const componentInstance = modalRef.componentInstance as AdHocSigningReminderComponent;

          componentInstance.documentBase = annual;
          componentInstance.annualPaymentDeadline = annualRow.paymentDeadline;

          return componentInstance.sendSigningAdHocReminder;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  openMarkAsSentModal(annualRecord: AnnualStatementRecord): void {
    if (annualRecord?.status != AnnualStatementStatus.ReadyToSend && annualRecord?.signingStatus != SigningStatus.SentViaEmail) {
      this.toastr.error("Incorrect status", "Error");
      return;
    }

    this.markSentSignedCommonService.markAsSent([annualRecord.documentId])
      .pipe(filter(result => result === true))
      .subscribe({
        next: (result) => {
          if (result) {
            this.toastr.success("Annual statement was marked as sent", "Success");
            this.reloadGrid();
          }
        },
        error: (error) => {
          console.warn('[markAsSent] error: ', error);
        }
      });
  }

  refreshCompanyDebt(companyId: string): void {
    this.companiesApiService.refreshDebts(companyId)
      .subscribe({
        next: () => this.toastr.success("Request sent", "Success"),
        error: (error) => {
          console.warn('[refreshCompanyDebt] error: ', error);
          this.toastr.error("Error while sending request", "Error");
        },
      });
  }

  openMarkAsSignedModal(annualRecord: AnnualStatementRecord): void {
    const inSignedProcess = annualRecord.signingStatus === SigningStatus.Sent;

    this.markSentSignedCommonService.markAsSignedStatement(annualRecord.documentId, inSignedProcess, null)
      .subscribe({
        next: () => {
          this.reloadGrid();
        }
      });
  }

  openAdHocPaymentReminderModal(companyId: string): void {
    const annualRow = this.records().filter(row => row.companyId === companyId)[0];

    this.annualStatementsApiService.getAnnualById(annualRow.documentId)
      .pipe(
        switchMap(annual => {
          const modalRef = this.modalFormsService.openModal(AdHocPaymentReminderComponent);
          const instance = modalRef.componentInstance as AdHocPaymentReminderComponent;

          instance.company = annual.company;
          instance.documentBase = annual;
          instance.annualPaymentDeadline = annualRow.paymentDeadline;

          return instance.sendPaymentAdHocReminder;
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
  }

  openSelectRegenerateOptionModal(record?: AnnualStatementRecord): void {
    const modalRef = this.modalService.open(CommonActionsModalComponent, { size: 'sm' });
    const componentInstance = modalRef.componentInstance as CommonActionsModalComponent;

    const actions: IActionButtonConfig[] = [
      {
        text: 'Reprint annual statement (RA71)',
        action: of(true)
          .pipe(
            tap(() => {
              if (record)
                this.reprintAS(record.companyId);
              else
                this.reprintASBulk();
            })
          )
      },
      {
        text: 'Re-generate annual statement (480F)',
        action: of(true)
          .pipe(
            tap(() => {
              if (record)
                this.openConfirmRegenerateAnnualStatementModal(record);
              else
                this.regenerateFakeBulk();
            })
          )
      },
    ];

    componentInstance.config = {
      ...componentInstance.config,
      header: 'Re-generate Annual Statement',
      body: 'Please select re-generate option',
      actions
    };
  }

  reprintAS(companyId: string): void {
    this.annualStatementsApiService.reprintAS([companyId])
      .subscribe({
        next: () => this.toastr.success("Request sent", "Success"),
        error: () => this.toastr.error("Error while sending request", "Error"),
      });
  }

  openConfirmRegenerateAnnualStatementModal(record: AnnualStatementRecord): void {
    if (!record.debt) {
      const modalRef = this.modalService.open(CommonActionsModalComponent, { size: 'sm' });
      const componentInstance = modalRef.componentInstance as CommonActionsModalComponent;

      const actions: IActionButtonConfig[] = [
        {
          text: 'Yes',
          action: of(true)
            .pipe(
              tap(() => this.regenerate(record.companyId))
            )
        },
      ];

      componentInstance.config = {
        ...componentInstance.config,
        header: 'No debt warning',
        body: `The company currently has no debt. Would you like to regenerate the annual statement?`,
        actions,
        showCloseBtn: true,
        closeBtnText: 'No'
      };

      return;
    }

    this.regenerate(record.companyId);
  }

  regenerate(companyId: string): void {
    this.annualStatementsApiService.annualRegenerate(companyId)
      .subscribe({
        next: () => {
          this.reloadGrid();
          this.toastr.success("Annual Statement were regenerated", "Success");
        },
        error: () => this.toastr.error("Error while regenerationg annual statement", "Error"),
      });
  }

  // =====  Bulk Actions  =====

  maskAsPaidBulk() {
    const selectedIds = this.checkedAnnualStatements.map(x => x.documentId);
    this.annualStatementsApiService.markAsPaidBulk(selectedIds)
      .subscribe({
        next: () => {
          this.reloadGrid();
          this.toastr.success("Annual statements were marked as paid", "Success");
        },
        error: (err) => {
          this.toastr.error("Error happen.", "Error");
          console.error(err);
        }
      });
  }

  markAsSentBulk(): void {
    const selectedIds = this.checkedAnnualStatements.map(x => x.documentId);

    if (this.checkedAnnualStatements.some(x => x.status !== AnnualStatementStatus.ReadyToSend)) {
      this.toastr.error("Incorrect status", "Error");
      return;
    }

    this.markSentSignedCommonService.markAsSent(selectedIds)
      .pipe(filter(result => result === true))
      .subscribe({
        next: (result) => {
          if (result) {
            this.toastr.success("Annual statements were marked as sent", "Success");
            this.reloadGrid();
          }
        }
      });
  }

  markAsSignedBulkAction(): void {
    const selectedIds = this.checkedAnnualStatements.map(x => x.documentId);
    const inSignedProcess = this.checkedAnnualStatements.some(x => x.signingStatus === SigningStatus.Sent);

    this.markSentSignedCommonService.markAsSignedStatementBulk(selectedIds, inSignedProcess, null)
      .subscribe({
        next: () => this.reloadGrid(),
        error: (error) => {
          console.error('[markAsSignedBulkAction] error: ', error);
          this.toastr.error("Download Annual Statements error", "Error");
        }
      });
  }

  downloadDocumentsBulk(): void {
    const selectedIds = this.checkedAnnualStatements.map(x => x.documentId);

    this.filesApiService.downloadDocumentBulk(selectedIds)
      .subscribe({
        next: (res) => {
          if (res) {
            const currentDate = formatDate(new Date, 'dd_MM_yyyy', 'en-US');
            const fileName = 'Annual_Statements_' + currentDate + '.zip';
            downloadBase64File(res, fileName);
            this.toastr.success("Annual Statements have been success downloaded", "Success");
          }
        },
        error: (error: HttpErrorResponse) => {
          console.error(error);
          this.toastr.error("Download Annual Statements error", "Error");
        }
      });
  }

  ignoreBulk(): void {
    const selectedIds = this.checkedAnnualStatements.map(x => x.companyId);
    this.annualStatementsApiService.ignoreBulk(selectedIds)
      .subscribe({
        next: () => {
          this.reloadGrid();
          this.toastr.success("Annual statements were ignored", "Success");
        },
        error: (error) => {
          this.toastr.error("Error happen.", "Error");
          console.error('[ignoreBulk] error: ', error);
        }
      });
  }

  bulkSendForSigning(): void {
    if (this.checkedAnnualStatements.some((annual) => annual.status !== AnnualStatementStatus.Sent && annual.signingStatus !== SigningStatus.ReadyToSend)) {
      this.toastr.error('Only forms with \'Ready to Send\' signing status can be sent for signing. Please review the selected options and try again');
      return;
    }

    const documentsIds = this.checkedAnnualStatements.map((annual) => annual.documentId);
    const linkParams = { queryParams: { [annualStatementsQueryParamKey]: documentsIds } };

    void this.router.navigate(['/bulk-send-annual-statement-for-signing'], linkParams);
  }

  // =====  Visibility rules  =====

  isMarkAsSentVisible(): boolean {
    return this.checkedAnnualStatements.every(x => x.status === AnnualStatementStatus.ReadyToSend);
  }

  isMarkAsSignedVisible(): boolean {
    return this.checkedAnnualStatements.every(x => x.status === AnnualStatementStatus.ReadyToSend || x.status === AnnualStatementStatus.Sent || x.isPaid);
  }

  reprintASBulk(): void {
    const selectedIds = this.checkedAnnualStatements.map(x => x.companyId);

    this.annualStatementsApiService.reprintAS(selectedIds)
      .subscribe({
        next: () => this.toastr.success("Request sent", "Success"),
        error: () => this.toastr.error("Error while refreshing companies", "Error"),
      });
  }

  regenerateFakeBulk(): void {
    const selectedIds = this.checkedAnnualStatements.map(x => x.companyId);

    this.annualStatementsApiService.annualRegenerateBulk(selectedIds)
      .subscribe({
        next: () => {
          this.reloadGrid();
          this.toastr.success("Annual Statements were regenerated", "Success");
        },
        error: (error) => {
          console.warn('[regenerateFakeBulk] error: ', error);
          this.toastr.error("Error while regeneration annual statements", "Error");
        },
      });
  }

  // =====  Grid  =====

  readonly ExportTypeEnum = ExportTypeEnum;
  readonly defaultPageSize = PageSizeService.defaultPageSize;
  readonly cacheBlockSize = 20;
  readonly pinFirstColumnName = 'Company Name & ACN';
  readonly getRowId: GetRowIdFunc = (params: GetRowIdParams<AnnualStatementRecord>) => params.data.documentId;
  readonly gridOptions: GridOptions = {
    overlayNoRowsTemplate: '<span class="no-rows-overlay">No data available</span>',
    getRowId: this.getRowId
  };

  profileUserNameList: string[] = this.organisationService.getCachedProfileUsers().map(u => u?.fullName);
  tagsList: string[] = [];

  colDefs = this.annualStatementsGridService.colDefs;
  totalRecords = this.annualStatementsGridService.totalRecords;
  dataSource = this.annualStatementsGridService.dataSource;

  onGridReady(gridApi: GridApi): void {
    this.annualStatementsGridService.setGridApi(gridApi);

    this.annualStatementsGridService.gridApi.addEventListener(
      'modelUpdated',
      () => {
        const totalPages = this.annualStatementsGridService.gridApi.paginationGetTotalPages();

        if (!totalPages) {
          this.annualStatementsGridService.gridApi.showNoRowsOverlay();
        } else {
          this.annualStatementsGridService.gridApi.hideOverlay();
        }
      });
  }

  selectAnnualStatements(annual: AnnualStatementRecord[]): void {
    this.checkedAnnualStatements = annual;
  }

  onSearch(searchText: string): void {
    this.annualStatementsGridService.setDataSource(searchText);
  }

  reloadGrid(): void {
    this.annualStatementsGridService.gridApi.deselectAll();
    this.annualStatementsGridService.gridApi.refreshServerSide();
  }

  setGridConfig(): void {
    this.annualStatementsGridService.setAnnualStatementPageFilter(this.pageConfig.key);
    this.annualStatementsGridService.setColDefsWithConfig(
      this.pageConfig.colDefsKeys,
      {
        width: 150,
        sortable: false,
        cellRenderer: AgAnnualActionsComponent,
        cellRendererParams: {
          pageFilter: this.pageConfig.key,
          send: this.openAnnualViewAndSendModal.bind(this),
          downloadDocuments: this.downloadDocuments.bind(this),
          archive: this.openArchiveModal.bind(this),
          ignore: this.ignore.bind(this),
          sendReminder: this.openAdHocSigningReminderModal.bind(this),
          sendPaymentReminder: this.openAdHocPaymentReminderModal.bind(this),
          regenerate: this.openSelectRegenerateOptionModal.bind(this),
          markAsSent: this.openMarkAsSentModal.bind(this),
          markAsSigned: this.openMarkAsSignedModal.bind(this),
          downloadCombinedPDF: this.downloadCombinedPDF.bind(this),
          refreshCompanyDebt: this.refreshCompanyDebt.bind(this),
        },
        menuTabs: []
      }
    );
  }

  setGridFilterOptions(): void {
    this.annualStatementsGridService.setGridFilterOptions(
      this.canBeOpened,
      this.profileUserNameList,
      this.tagsList
    );
  }

  // =====  Export  =====

  readonly sheetName: string = 'Annual statements';
  readonly excelExportParams = this.annualStatementsExportService.exportParamsXls();
  readonly excelStyles = this.annualStatementsExportService.excelStyles;

  exportSelectedAnnualStatementsToXls(): void {
    this.exportAnnualList(true, ExportTypeEnum.EXCEL);
  }

  exportSelectedAnnualStatementsToCsv(): void {
    this.exportAnnualList(true, ExportTypeEnum.CSV);
  }

  exportAnnualList(isBulkExport: boolean, exportType: ExportTypeEnum): void {
    // if (this.modalOpened()) return;

    this.annualStatementsExportService.exportAnnualList(
      this.annualStatementsGridService.gridApi,
      this.colDefs(),
      this.checkedAnnualStatements,
      this.totalRecords(),
      isBulkExport,
      exportType,
      this.sheetName
    );
  }
}
